import React, { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from '../utils/axiosInstance';
import {
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  useMediaQuery,
  Container,
  useTheme,
  alpha,
  CircularProgress,
  Fab,
  Zoom,
} from "@mui/material";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import SaveIcon from '@mui/icons-material/Save';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faBookmark, faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import 'dayjs/locale/hi';
import 'dayjs/locale/mr';
import 'dayjs/locale/kn';
import 'dayjs/locale/bn';
import 'dayjs/locale/te';
import 'dayjs/locale/ta';
import 'dayjs/locale/pa-in';
import 'dayjs/locale/gu';
import TimeClockWithTextField from "./TimeClockWithTextField";
import TemplatePicker from "./TemplatePicker";
import Navbar from "./Navbar";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  initializeSession,
  getSavedFormData,
  getUuid,
  setUuid,
  saveFormDataToSession,
  setLang,
  getLang,
} from "../service/AutoSave";
import CustomTextField from "./CustomTextField";
import HeightSelect from "./HeightSelect";
import CropperDialog from './CropperDialog';
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import i18n from 'i18next';
import config from '../config';
import { useNavigate } from 'react-router-dom';

// import { gtag_report_conversion } from '../utils/gtag';

const axios = axiosInstance;

const MAX_GALLERY_PHOTOS = 6;
const GALLERY_ASPECT_RATIO = 3 / 4;
const createEmptyGalleryState = () => Array(MAX_GALLERY_PHOTOS).fill(null);

// Remove old theme - using the global theme from App.js

// Reset visibility and order
const defaultVisibleFields = {
  personal: {
    dateOfBirth: false,
    birthTime: false,
    rashi: false,
    nakshatra: false,
    gotra: false,
    religion: false,
    caste: false,
    income: false,
    complexion: false,
    height: false,
    qualification: false,
    occupation: false,
    hobbies: false,
    seeking: false,
    familyBackground: false,
  },
  family: {
    fatherOccupation: false,
    motherOccupation: false,
    siblings: false,
  },
  contact: {
    address: false,
    email: false,
  },
};

const defaultPersonalFields = ["name", "birthPlace"];
const defaultFamilyFields = ["fatherName", "motherName"];
const defaultContactFields = ["contactPerson", "contactNumber"];

const BiodataForm = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t, i18n } = useTranslation();
  const language = getLang();
  const [currentLanguage, setCurrentLanguage] = useState(language || "en");
  const navigate = useNavigate();

  // Custom event system for template picker communication
  const [templateReloadTrigger, setTemplateReloadTrigger] = useState(0);
  const [isReloadInProgress, setIsReloadInProgress] = useState(false);

  // Debounced template reload function
  const debouncedTemplateReload = () => {
    if (isReloadInProgress) {
      console.log('Reload already in progress, waiting 2 seconds...');
      setTimeout(() => {
        setTemplateReloadTrigger(prev => prev + 1);
        setIsReloadInProgress(false);
      }, 2000);
    } else {
      setIsReloadInProgress(true);
      setTemplateReloadTrigger(prev => prev + 1);
      
      // Reset the flag after 2 seconds
      setTimeout(() => {
        setIsReloadInProgress(false);
      }, 2000);
    }
  };

  const handleCropperClose = () => {
    setShowCropper(false);
    setCropperImageSrc(null);
    setCropperContext({ type: 'profile', index: null });
    setCropperAspectRatio(1);
  };

  const initialFormData = {
    language: language || "en",
    name: "",
    dob: null, // string
    birthPlace: "",
    birthTime: null,
    rashi: "",
    nakshatra: "",
    gotra: "",
    religion: "",
    caste: "",
    income: "",
    complexion: "",
    height: "",
    qualification: "",
    occupation: "",
    hobbies: "",
    seeking: "",
    familyBackground: "",
    fatherName: "",
    fatherOccupation: "",
    motherName: "",
    motherOccupation: "",
    siblings: "",
    contactPerson: "",
    contactNumber: "",
    email: "",
    address: "",
    personalAdditions: [],
    personalDetailsOrder: defaultPersonalFields,
    familyAdditions: [],
    familyDetailsOrder: defaultFamilyFields,
    contactAdditions: [],
    contactDetailsOrder: defaultContactFields,
    visibleFields: defaultVisibleFields,
    template: {},
    photoPath: "",
    galleryPhotos: createEmptyGalleryState(),
  };

  const [languageSelectorVisible, setLanguageSelectorVisible] = useState(!language);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [isPhotoSectionVisible, setIsPhotoSectionVisible] = useState(true);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState(null);
  const [cropperContext, setCropperContext] = useState({ type: 'profile', index: null });
  const [cropperAspectRatio, setCropperAspectRatio] = useState(1);
  const [galleryPreviews, setGalleryPreviews] = useState(createEmptyGalleryState());
  const [galleryPhotoPaths, setGalleryPhotoPaths] = useState(createEmptyGalleryState());
  const [galleryLoading, setGalleryLoading] = useState(Array(MAX_GALLERY_PHOTOS).fill(false));
  const galleryLoadedRef = useRef(false);
  const [isFormSaved, setIsFormSaved] = useState(false);
  const [isTemplateFree, setIsTemplateFree] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isPurchased, setPurchased] = useState(false);
  const [showFloatingSave, setShowFloatingSave] = useState(false);
  const lastSavedFormDataRef = useRef(null);
  const [isLoadingBiodata, setIsLoadingBiodata] = useState(false);

  // Read query parameters and handle id/templateId (must be declared before useEffects)
  const urlParams = new URLSearchParams(window.location.search);
  const queryId = urlParams.get("id");
  const queryTemplateId = urlParams.get("templateId");

  // Helper function to set cookie
  const setCookie = (name, value, days = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  useEffect(() => {
    i18n.changeLanguage(currentLanguage);
  }, [currentLanguage, i18n]);

  // Fetch biodata details by ID when id query parameter is present
  useEffect(() => {
    const fetchBiodataById = async () => {
      if (!queryId) return;

      try {
        setIsLoadingBiodata(true);
        const apiUrl = config.apiBaseUrl;
        const response = await axios.get(`${apiUrl}/sb/api/biodata/${queryId}`);
        
        if (response.status === 200 && response.data) {
          const biodataData = response.data?.data || response.data;
          
          // Update form data with fetched biodata, using initialFormData as fallback
          const updatedFormData = {
            ...initialFormData,
            name: biodataData.name || initialFormData.name,
            dob: biodataData.dob || initialFormData.dob,
            birthPlace: biodataData.birthPlace || initialFormData.birthPlace,
            birthTime: biodataData.birthTime || initialFormData.birthTime,
            rashi: biodataData.rashi || initialFormData.rashi,
            nakshatra: biodataData.nakshatra || initialFormData.nakshatra,
            gotra: biodataData.gotra || initialFormData.gotra,
            religion: biodataData.religion || initialFormData.religion,
            caste: biodataData.caste || initialFormData.caste,
            income: biodataData.income || initialFormData.income,
            complexion: biodataData.complexion || initialFormData.complexion,
            height: biodataData.height || initialFormData.height,
            qualification: biodataData.qualification || initialFormData.qualification,
            occupation: biodataData.occupation || initialFormData.occupation,
            hobbies: biodataData.hobbies || initialFormData.hobbies,
            seeking: biodataData.seeking || initialFormData.seeking,
            familyBackground: biodataData.familyBackground || initialFormData.familyBackground,
            fatherName: biodataData.fatherName || initialFormData.fatherName,
            fatherOccupation: biodataData.fatherOccupation || initialFormData.fatherOccupation,
            motherName: biodataData.motherName || initialFormData.motherName,
            motherOccupation: biodataData.motherOccupation || initialFormData.motherOccupation,
            siblings: biodataData.siblings || initialFormData.siblings,
            contactPerson: biodataData.contactPerson || initialFormData.contactPerson,
            contactNumber: biodataData.contactNumber || initialFormData.contactNumber,
            email: biodataData.email || initialFormData.email,
            address: biodataData.address || initialFormData.address,
            personalAdditions: biodataData.personalAdditions || initialFormData.personalAdditions,
            personalDetailsOrder: biodataData.personalDetailsOrder || initialFormData.personalDetailsOrder,
            familyAdditions: biodataData.familyAdditions || initialFormData.familyAdditions,
            familyDetailsOrder: biodataData.familyDetailsOrder || initialFormData.familyDetailsOrder,
            contactAdditions: biodataData.contactAdditions || initialFormData.contactAdditions,
            contactDetailsOrder: biodataData.contactDetailsOrder || initialFormData.contactDetailsOrder,
            visibleFields: biodataData.visibleFields || initialFormData.visibleFields,
            template: biodataData.template || initialFormData.template,
            photoPath: biodataData.photoPath || initialFormData.photoPath,
            galleryPhotos: biodataData.galleryPhotos || initialFormData.galleryPhotos,
            dateOfBirth: biodataData.dateOfBirth ? dayjs(biodataData.dateOfBirth) : null,
            language: biodataData.language || initialFormData.language,
          };

          setFormData(updatedFormData);
          saveFormDataToSession(updatedFormData);

          // Update visible fields
          if (biodataData.visibleFields) {
            setVisibleFields(biodataData.visibleFields);
          }

          // If template is present in fetched data, set it
          if (biodataData.template && biodataData.template._id) {
            setSelectedTemplate(biodataData.template);
            const isFree = biodataData.template?.pricing?.isFree || false;
            setIsTemplateFree(isFree);
          }
        }
      } catch (error) {
        console.error("Error fetching biodata details:", error);
        toast.error("Failed to load biodata details. Please try again.");
      } finally {
        setIsLoadingBiodata(false);
      }
    };

    fetchBiodataById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryId]); // Only run when queryId changes

  // Initialize language based on query parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const lang = urlParams.get("lang");

    // Set the language if valid; otherwise, default to 'en'
    const supportedLanguages = ["en", "hi", "mr", "gu", "kn", "ta", "te", "or", "bn", "pa"];
    const selectedLanguage = supportedLanguages.includes(lang) ? lang : "en";

    // Change the language
    i18n.changeLanguage(selectedLanguage);
  }, [i18n]);

  // Initialize session and handle UUID from query parameter
  let initialUuid = getUuid(initialFormData);
  if (queryId) {
    // Replace UUID with id from query parameter
    initialUuid = queryId;
    setUuid(queryId, initialFormData);
  }

  // Set cookie with biodata ID (run once on mount)
  useEffect(() => {
    if (queryId) {
      setCookie("biodataId", queryId);
    } else {
      const currentUuid = getUuid(initialFormData);
      setCookie("biodataId", currentUuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  initializeSession(initialFormData, initialUuid);
  const savedFormData = getSavedFormData(initialFormData);

  let uuid = initialUuid;
  const [formData, setFormData] = useState({
    ...savedFormData,
    dateOfBirth: savedFormData.dateOfBirth ? dayjs(savedFormData.dateOfBirth) : null,
  });

  const [visibleFields, setVisibleFields] = useState({
    personal: formData.visibleFields ? formData.visibleFields.personal : defaultVisibleFields.personal,
    family: formData.visibleFields ? formData.visibleFields.family : defaultVisibleFields.family,
    contact: formData.visibleFields ? formData.visibleFields.contact : defaultVisibleFields.contact,
  });

  const updateGallerySlot = useCallback((slotIndex, payload) => {
    setGalleryPreviews((prev) => {
      const next = [...prev];
      next[slotIndex] = payload?.photoUrl || null;
      return next;
    });

    setGalleryPhotoPaths((prev) => {
      const next = [...prev];
      next[slotIndex] = payload?.photoPath || null;
      return next;
    });

    setFormData((prev) => {
      const galleryPaths = Array.isArray(prev.galleryPhotos)
        ? [...prev.galleryPhotos]
        : createEmptyGalleryState();
      galleryPaths[slotIndex] = payload?.photoPath || null;
      const updatedForm = { ...prev, galleryPhotos: galleryPaths };
      saveFormDataToSession(updatedForm);
      return updatedForm;
    });
  }, [setFormData]);

  const clearGallerySlot = useCallback((slotIndex) => {
    setGalleryPreviews((prev) => {
      const next = [...prev];
      next[slotIndex] = null;
      return next;
    });

    setGalleryPhotoPaths((prev) => {
      const next = [...prev];
      next[slotIndex] = null;
      return next;
    });

    setFormData((prev) => {
      const galleryPaths = Array.isArray(prev.galleryPhotos)
        ? [...prev.galleryPhotos]
        : createEmptyGalleryState();
      galleryPaths[slotIndex] = null;
      const updatedForm = { ...prev, galleryPhotos: galleryPaths };
      saveFormDataToSession(updatedForm);
      return updatedForm;
    });
  }, [setFormData]);

  const resetGalleryState = useCallback(() => {
    setGalleryPreviews(createEmptyGalleryState());
    setGalleryPhotoPaths(createEmptyGalleryState());
    setGalleryLoading(Array(MAX_GALLERY_PHOTOS).fill(false));
    galleryLoadedRef.current = false;
  }, []);

  const fetchGalleryPhotos = useCallback(async () => {
    if (!uuid) {
      return;
    }

    try {
      const response = await axios.get(`${config.apiBaseUrl}/sb/biodata/gallery/${uuid}`);
      if (response.status === 200) {
        const rawPhotos =
          response.data?.data?.photos ||
          response.data?.data?.galleryImages ||
          response.data?.data ||
          response.data;
        const previews = createEmptyGalleryState();
        const paths = createEmptyGalleryState();

        if (Array.isArray(rawPhotos)) {
          rawPhotos.forEach((item, index) => {
            const slotFromPayload =
              item?.slot ?? item?.position ?? item?.index ?? item?.sequence ?? item?.order;

            let slotIndex = index;
            if (Number.isFinite(Number(slotFromPayload))) {
              const numericSlot = Number(slotFromPayload);
              // API returns 1-based positions; convert to zero-based index
              const zeroBasedSlot = numericSlot > 0 ? numericSlot - 1 : numericSlot;
              slotIndex = zeroBasedSlot;
            }

            slotIndex = Math.max(0, Math.min(MAX_GALLERY_PHOTOS - 1, slotIndex));

            if (slotIndex < MAX_GALLERY_PHOTOS) {
              const rawPath = item?.photoPath || item?.path || null;
              const normalizedPath = rawPath
                ? rawPath
                    .replace(/^(\.\.\/)+/, '')
                    .replace(/\/{2,}/g, '/')
                    .replace(/^content\/biodata\//, 'content/biodata/')
                : null;

              const urlCandidate =
                item?.photoUrl ||
                item?.url ||
                item?.preview ||
                (normalizedPath
                  ? `${config.serverUrl?.replace(/\/$/, '')}/${normalizedPath.replace(/^\//, '')}`
                  : null) ||
                null;

              previews[slotIndex] = urlCandidate;
              paths[slotIndex] = normalizedPath;
            }
          });
        }

        setGalleryPreviews(previews);
        setGalleryPhotoPaths(paths);
        setGalleryLoading(Array(MAX_GALLERY_PHOTOS).fill(false));

        setFormData((prev) => {
          const prevGallery = prev.galleryPhotos || createEmptyGalleryState();
          if (JSON.stringify(prevGallery) === JSON.stringify(paths)) {
            return prev;
          }
          const updatedForm = { ...prev, galleryPhotos: paths };
          saveFormDataToSession(updatedForm);
          return updatedForm;
        });

        galleryLoadedRef.current = true;
      }
    } catch (error) {
      console.error("Error fetching gallery photos ", error);
      galleryLoadedRef.current = true;
    }
  }, [uuid, setFormData]);

  useEffect(() => {
    if (
      formData?.visibleFields &&
      JSON.stringify(formData.visibleFields) !== JSON.stringify(visibleFields)
    ) {
      setVisibleFields(formData.visibleFields);
    }
  }, [formData, visibleFields]);

  const handleReset = () => {
    // do not change the order
    setIsFormSaved(false);
    lastSavedFormDataRef.current = null;
    //handleRemovePhoto();
    setLanguageSelectorVisible(true);
    setFormData({ ...initialFormData });
    localStorage.removeItem("biodataForm");
    initializeSession(initialFormData, uuid);
    resetGalleryState();
  };

  useEffect(() => {
    const fetchProfilePhoto = async () => {
      try {
        if (formData.photoPath) {
          const response = await axios.get(config.apiBaseUrl + '/sb/biodata/photo/' + uuid);
          if (response.status === 200) setPhotoPreview(response.data);
          else console.log('No profile photo found');
        }
      } catch (err) {
        console.error("Error fetching profile photo ");
      }
    };
    fetchProfilePhoto();
  }, [formData.photoPath, uuid]);

  useEffect(() => {
    if (!galleryLoadedRef.current) {
      fetchGalleryPhotos();
    }
  }, [fetchGalleryPhotos]);

  // Scroll detection for floating save button
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY || document.documentElement.scrollTop;
      setShowFloatingSave(scrollPosition > 100); // Show button after scrolling 100px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Detect unsaved changes and enable save button
  useEffect(() => {
    if (isFormSaved && lastSavedFormDataRef.current) {
      const currentFormDataString = JSON.stringify({
        ...formData,
        dob: formData.dob,
        language: i18n.language,
      });
      
      if (currentFormDataString !== lastSavedFormDataRef.current) {
        setIsFormSaved(false);
      }
    }
  }, [formData, isFormSaved, i18n.language]);

  const handlePhotoChange = async (e, options = { type: 'profile', index: null }) => {
    const { type = 'profile', index = null } = options;
    const isGallery = type === 'gallery';
    const file = e.target.files[0];
    if (file) {
      try {
        if (!uuid) {
          console.log(uuid);
          uuid = getUuid(initialFormData);
        }
        if (!isGallery) {
          // Upload original image immediately for profile photo
          const originalFormData = new FormData();
          originalFormData.append('originalPhoto', file);
          const apiUrl = config.apiBaseUrl;
          await axios.put(
            `${apiUrl}/sb/upload/biodata/${uuid}/original-photo`,
            originalFormData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        } else if (isGallery && index !== null) {
          const slotIndex = index;
          setGalleryLoading((prev) => {
            const next = [...prev];
            next[slotIndex] = true;
            return next;
          });

          const galleryFormData = new FormData();
          galleryFormData.append('photo', file);
          const apiUrl = config.apiBaseUrl;
          const response = await axios.put(
            `${apiUrl}/sb/upload/biodata/${uuid}/gallery/${slotIndex + 1}`,
            galleryFormData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );

          const photoPath = response.data?.data?.photoPath || response.data?.photoPath || null;
          const photoUrl =
            response.data?.data?.photoUrl ||
            response.data?.photoUrl ||
            URL.createObjectURL(file);

          updateGallerySlot(slotIndex, { photoUrl, photoPath });
          setGalleryLoading((prev) => {
            const next = [...prev];
            next[slotIndex] = false;
            return next;
          });

          e.target.value = null;
          fetchGalleryPhotos();
          return;
        }
      } catch (error) {
        // toast.error('Original image upload failed');
        console.error(error);
        if (isGallery && index !== null) {
          const slotIndex = index;
          setGalleryLoading((prev) => {
            const next = [...prev];
            next[slotIndex] = false;
            return next;
          });
          toast.error('Failed to upload gallery photo. Please try again.');
        }
      }

      if (isGallery) {
        e.target.value = null;
        return;
      }

      e.target.value = null;
      const reader = new FileReader();
      reader.onload = (e) => {
        setCropperImageSrc(e.target.result);
        setCropperContext({ type, index: isGallery ? index : null });
        setCropperAspectRatio(isGallery ? GALLERY_ASPECT_RATIO : 1);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropSave = async (croppedFile) => {
    try {
      const formData = new FormData();
      formData.append('photo', croppedFile);
      const apiUrl = config.apiBaseUrl;

      if (cropperContext.type === 'gallery' && cropperContext.index !== null) {
        const slotIndex = cropperContext.index;
        setGalleryLoading((prev) => {
          const next = [...prev];
          next[slotIndex] = true;
          return next;
        });

        const response = await axios.put(
          `${apiUrl}/sb/upload/biodata/${uuid}/gallery/${slotIndex + 1}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        const photoUrl = response.data?.data?.photoUrl || response.data?.photoUrl || URL.createObjectURL(croppedFile);
        const photoPath = response.data?.data?.photoPath || response.data?.photoPath || null;
        updateGallerySlot(slotIndex, { photoUrl, photoPath });
        setGalleryLoading((prev) => {
          const next = [...prev];
          next[slotIndex] = false;
          return next;
        });

        fetchGalleryPhotos();
        // toast.success('Gallery photo saved successfully');
      } else {
        const response = await axios.put(
          `${apiUrl}/sb/upload/biodata/${uuid}/photo`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        setPhoto(croppedFile);
        handlePhotoUploaded(response.data);
        setPhotoPreview(response.data?.data?.photoUrl || response.data);
        // toast.success('Image saved successfully');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to save photo. Please try again.');
      if (cropperContext.type === 'gallery' && cropperContext.index !== null) {
        const slotIndex = cropperContext.index;
        setGalleryLoading((prev) => {
          const next = [...prev];
          next[slotIndex] = false;
          return next;
        });
      }
    }
  };

  const handlePhotoUploaded = (data) => {
    const updatedFormData = { ...formData, photoPath: data?.data.photoPath };
    saveFormDataToSession(updatedFormData);
    debouncedTemplateReload();
    setFormData({ ...formData, photoPath: data?.data.photoPath });
  };

  function isFormValid() {
    if (!formData.name?.trim()) {
      toast.error(t("enterName"));
      return false;
    }

    if (!formData.birthPlace?.trim()) {
      toast.error(t("enterPlaceOfBirth"));
      return false;
    }

    if (!formData.contactNumber?.trim()) {
      toast.error(t("enterContactPersonNumber"));
      return false;
    }

    if (!formData.contactPerson?.trim()) {
      toast.error(t("enterContactPerson"));
      return false;
    }

    if (!formData.template?._id && !isFormSaved) {
      toast.error(t("pleaseChooseTemplate"));
      return false;
    }
    return true;
  }

  const buyNow = async () => {
    try {
      if (!isFormValid()) {
        return;
      }

      if (!selectedTemplate || selectedTemplate.pricing.isFree) {
        toast.error(t("pleaseChooseTemplate"));
        return;
      }

      const { _id: templateId, name, thumbnail, pricing } = selectedTemplate;
      const { amount, currency } = pricing;

      navigate('/order-confirm', {
        state: {
          uuid,
          template: selectedTemplate
        }
      });

    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to proceed. Please try again.");
    }
  };

  const downloadBiodata = async () => {
    try {
      if (!isFormValid()) {
        return;
      }

      setGeneratingPdf(true);

      const apiUrl = config.apiBaseUrl;
      const version = formData.template?.version || 'v1';
      let url = `${apiUrl}/generate-pdf/${uuid}/${version}?t=${formData.template._id}`;
      window.open(url, '_blank');
      // Trigger Google Ads conversion tracking
      // gtag_report_conversion('https://www.createshaadibiodata.com/');
    } catch (error) {
      toast.error('Please enter your details first.');
      console.error(error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const removeProfilePhoto = async () => {
    try {
      if (uuid) {
        const response = await axios.delete(config.apiBaseUrl + '/sb/biodata/photo/' + uuid);
        if (response.status === 200) setPhotoPreview(null);
        else console.error('Unble to remove profile photo');
      }
    } catch (err) {
      console.error("Error removing profile photo ");
    }
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    removeProfilePhoto(uuid);
    const updatedFormData = { ...formData, photoPath: '' };
    saveFormDataToSession(updatedFormData);
    debouncedTemplateReload();
    setFormData({ ...formData, photoPath: '' });
  };

  const handleRemoveGalleryPhoto = async (slotIndex) => {
    try {
      setGalleryLoading((prev) => {
        const next = [...prev];
        next[slotIndex] = true;
        return next;
      });

      await axios.delete(`${config.apiBaseUrl}/sb/biodata/gallery/${uuid}/${slotIndex + 1}`);
      clearGallerySlot(slotIndex);

      fetchGalleryPhotos();

      setGalleryLoading((prev) => {
        const next = [...prev];
        next[slotIndex] = false;
        return next;
      });
      // toast.success('Gallery photo removed');
    } catch (error) {
      console.error("Error removing gallery photo ", error);
      setGalleryLoading((prev) => {
        const next = [...prev];
        next[slotIndex] = false;
        return next;
      });
      toast.error('Failed to remove gallery photo. Please try again.');
    }
  };

  const updateFormData = (e) => {
    console.log('updateFormData called:', e.target.name, e.target.value);
    const data = { ...formData, [e.target.name]: e.target.value };
    setFormData(data);
    saveFormDataToSession(data);
    debouncedTemplateReload();
  }

  const handleBlur = (e) => {
    console.log('handleBlur called:', e.target.name, e.target.value);
    updateFormData(e);
  }

  const handleChange = (e) => {
    console.log('handleChange called:', e.target.name, e.target.value);
    updateFormData(e);
  };

  const formatOdiaDate = (date) => {
    const odiaMonths = [
      'ଜାନୁଆରୀ', 'ଫେବୃଆରୀ', 'ମାର୍ଚ୍ଚ',
      'ଅପ୍ରେଲ', 'ମଇ', 'ଜୁନ',
      'ଜୁଲାଇ', 'ଅଗଷ୍ଟ', 'ସେପ୍ଟେମ୍ବର',
      'ଅକ୍ଟୋବର', 'ନଭେମ୍ବର', 'ଡିସେମ୍ବର'
    ];

    const d = dayjs(date);
    return `${d.date()} ${odiaMonths[d.month()]}, ${d.year()}`;
  };

  const getTranslatedString = (key, options = {}) => {
    return i18n.t(key, options);
  };

  const handleDateChange = (date) => {
    let lo = currentLanguage;
    let dob;

    if (lo === 'pa') lo = 'pa-in';
    if (lo === 'or') {
      dob = formatOdiaDate(date);
    } else {
      dob = dayjs(date)?.locale(lo).format("D MMMM, YYYY");
    }

    // const test = getTranslatedString('name');
    // console.log(test);
    console.log('Locale: ' + lo + ': ' + dob);
    setFormData({ ...formData, dob: dob, dateOfBirth: date });
    debouncedTemplateReload();
  };

  const handleAddField = (section) => {
    const sectionKey = `${section}Additions`;
    const currentAdditions = formData[sectionKey] || [];
    setFormData({
      ...formData,
      [sectionKey]: [...currentAdditions, { key: "", value: "" }],
    });
  }

  const handleRemoveField = (section, index) => {
    const sectionKey = `${section}Additions`;
    const currentAdditions = formData[sectionKey] || [];
    const updatedFields = [...currentAdditions];
    updatedFields.splice(index, 1);
    const updatedFormData = { ...formData, [sectionKey]: updatedFields };
    saveFormDataToSession(updatedFormData);
    setFormData(updatedFormData);
    debouncedTemplateReload();
  };

  const handleFieldChange = (section, index, field, value) => {
    const sectionKey = `${section}Additions`;
    const currentAdditions = formData[sectionKey] || [];
    const updatedFields = [...currentAdditions];
    updatedFields[index][field] = value;
    setFormData({ ...formData, [sectionKey]: updatedFields });
    debouncedTemplateReload();
  };

  const toggleFieldVisibility = (section, field) => {
    // Ensure visibleFields is initialized if undefined or null
    const initialVisibleFields = formData.visibleFields || {
      personal: {},
      family: {},
      contact: {},
    };

    // Update visibleFields immutably
    const vs = { ...initialVisibleFields };
    vs[section] = { ...vs[section], [field]: !(vs[section]?.[field] ?? false) };

    // Update section order
    const sectionKey = `${section}DetailsOrder`;
    const currentOrder = formData[sectionKey] || []; // Ensure currentOrder is initialized as an empty array
    let updatedOrder;

    if (vs[section][field] && !currentOrder.includes(field)) {
      // Add the field to the order if it's being shown
      updatedOrder = [...currentOrder, field];
    } else if (!vs[section][field]) {
      // Remove the field from the order if it's being hidden
      updatedOrder = currentOrder.filter((item) => item !== field);
    } else {
      updatedOrder = currentOrder;
    }

    setFormData({
      ...formData,
      visibleFields: vs,
      [sectionKey]: updatedOrder,
    });
    debouncedTemplateReload();
  };

  const submit = async (payload) => {
    console.log('submit');
    try {
      if (!payload?.template?._id) {
        toast.info("Please choose a template by clicking on one of the template image!");
        return;
      }
      const apiUrl = config.apiBaseUrl;
      await axios.put(`${apiUrl}/sb/api/biodata/${uuid}`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      // Store the saved form data for comparison
      lastSavedFormDataRef.current = JSON.stringify(payload);
      setIsFormSaved(true);
    } catch (error) {
      toast.error(error.message);
      console.error(error);
    }
  };

  const onTemplateSelection = async (selectedTemplate, isPurchased) => {
    if (!selectedTemplate) return;
    try {
      setSelectedTemplate(selectedTemplate);
      setPurchased(isPurchased);
      const isFree = selectedTemplate?.pricing?.isFree || false;
      setIsTemplateFree(isFree);

      const updatedFormData = {
        ...formData, template: selectedTemplate || {},
        dob: formData.dob,
        language: i18n.language,
      };
      saveFormDataToSession(updatedFormData);
      submit(updatedFormData);
      // Use functional state update to ensure latest formData is used
      setFormData((prevFormData) => {
        const updatedFormData = { ...prevFormData, template: selectedTemplate || {} };
        saveFormDataToSession(updatedFormData); // Save updated data to session and local storage
        setIsFormSaved(true);
        return updatedFormData;
      });
    } catch (error) {
      toast.error(error.message);
      console.error('Error:', error);
    }
  };

  const formatFieldName = (label) => {
    return label.charAt(0).toLowerCase() + label.slice(1).replace(/\s+/g, "");
  };

  const renderPersonalDetailsTags = () => {
    const tags = Object.keys(defaultVisibleFields.personal || {}).map((field) => (
      <Button
        key={field}
        variant={formData.visibleFields && formData.visibleFields.personal && formData.visibleFields.personal[field] ? "contained" : "outlined"}
        color="primary"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          toggleFieldVisibility("personal", field)
        }}
        sx={{ textTransform: 'none', borderRadius: 16, fontSize: '0.8rem' }}
      >
        {t(field)}
      </Button>
    ));
    return (
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ mr: 2, display: 'block', width: '100%' }}>
          {t("clickTagsToAddFields")}
        </Typography>
        {tags}
      </Box>
    );
  };

  const renderFamilyDetailsTags = () => {
    const tags = Object.keys(defaultVisibleFields.family || {}).map((field) => (
      <Button
        key={field}
        variant={formData.visibleFields && formData.visibleFields.family && formData.visibleFields.family[field] ? "contained" : "outlined"}
        color="primary"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          toggleFieldVisibility("family", field)
        }}
        sx={{ textTransform: 'none', borderRadius: 16, fontSize: '0.8rem' }}
      >
        {t(field)}
      </Button>
    ));
    return (
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ mr: 2, width: '100%' }}>
          {t("clickTagsToAddFields")}
        </Typography>
        {tags}
      </Box>
    );
  };

  const renderContactDetailsTags = () => {
    const tags = Object.keys(defaultVisibleFields.contact || {}).map((field) => (
      <Button
        key={field}
        variant={formData.visibleFields && formData.visibleFields.contact && formData.visibleFields.contact[field] ? "contained" : "outlined"}
        color="primary"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          toggleFieldVisibility("contact", field)
        }}
        sx={{ textTransform: 'none', borderRadius: 16, fontSize: '0.8rem' }}
      >
        {t(field)}
      </Button>
    ));
    return (
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ mr: 2, width: '100%' }}>
          {t("clickTagsToAddFields")}
        </Typography>
        {tags}
      </Box>
    );
  };

  const handleTimeChange = (newTime) => {
    setFormData({ ...formData, birthTime: newTime });
    debouncedTemplateReload();
  };

  const handleSave = async () => {
    const updatedFormData = {
      ...formData,
      dob: formData.dob,
      language: i18n.language,
    };
    saveFormDataToSession(updatedFormData);
    await submit(updatedFormData);
    toast.success(t("formSavedSuccessfully") || "Form saved successfully!");
  };

  const handleLanguageChange = (lang) => {
    setCurrentLanguage(lang);
    setIsFormSaved(false);
    setLanguageSelectorVisible(true);
    setFormData({ ...initialFormData });
    // setFormData({ ...formData, language: lang });
    saveFormDataToSession({ ...initialFormData, language: lang });

    // Redirect to the same URL with the new language query parameter
    const currentUrl = window.location.href.split('?')[0]; // Remove existing query params
    window.location.href = `${currentUrl}?lang=${lang}`;
  };

  const blinkingAnimation = {
    '@keyframes blink': {
      '0%, 100%': { opacity: 1 }, // Fully visible
      '50%': { opacity: 0 },      // Fully transparent
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        <Navbar />

        {/* Main Content Container */}
        <Container maxWidth="xl" sx={{ py: 3, px: { xs: 1, sm: 2, md: 3 } }}>
          <Grid container spacing={3}>
            {/* Main Form Section */}
            <Grid item xs={12} lg={9}>
              {/* Photo Section - Mobile */}
              <Box sx={{
                display: { xs: 'block', lg: 'none' },
                mb: 3,
                textAlign: 'center'
              }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  border: '1px solid',
                  borderColor: 'divider',
                }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    style={{ display: 'none' }}
                    id="photo-upload-mobile"
                  />
                  <label htmlFor="photo-upload-mobile">
                    <Avatar
                      src={photoPreview}
                      sx={{
                        width: 140,
                        height: 140,
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        bgcolor: 'background.paper',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        mb: 1.5,
                        '&:hover': {
                          transform: 'scale(1.02)',
                          transition: 'transform 0.2s ease-in-out',
                        }
                      }}
                    >
                      {!photoPreview && <AddIcon fontSize="large" />}
                    </Avatar>
                  </label>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t("addPhotoMessage")}
                  </Typography>
                  {photoPreview && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemovePhoto}
                      size="small"
                    >
                      Remove Photo
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Gallery Section - Mobile */}
              <Box
                sx={{
                  display: { xs: 'block', lg: 'none' },
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>
                    {t("photoGallery")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    {t("photoGalleryDescription")}
                  </Typography>
                  <Grid container spacing={2} justifyContent="center">
                    {Array.from({ length: MAX_GALLERY_PHOTOS }).map((_, index) => {
                      const inputId = `gallery-upload-mobile-${index}`;
                      const hasPreview = !!galleryPreviews[index];
                      return (
                        <Grid item xs={4} key={`gallery-mobile-${index}`}>
                          <Stack spacing={1.5} alignItems="center">
                            <input
                              type="file"
                              accept="image/*"
                              id={inputId}
                              style={{ display: 'none' }}
                              onChange={(e) => handlePhotoChange(e, { type: 'gallery', index })}
                            />
                            <label htmlFor={inputId}>
                              <Box
                                sx={{
                                  width: 100,
                                  height: 120,
                                  borderRadius: 2,
                                  border: '2px dashed',
                                  borderColor: hasPreview ? 'primary.light' : 'primary.main',
                                  bgcolor: 'background.paper',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  boxShadow: hasPreview ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
                                  transition: 'transform 0.2s ease-in-out',
                                  '&:hover': {
                                    transform: 'scale(1.02)',
                                  },
                                }}
                              >
                                {galleryLoading[index] && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      inset: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: 'rgba(0,0,0,0.45)',
                                    }}
                                  >
                                    <CircularProgress size={26} sx={{ color: '#fff' }} />
                                  </Box>
                                )}
                                {hasPreview ? (
                                  <Box
                                    component="img"
                                    src={galleryPreviews[index]}
                                    alt={`Gallery ${index + 1}`}
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      objectPosition: 'center',
                                    }}
                                  />
                                ) : (
                                  <Stack spacing={1} alignItems="center">
                                    <AddIcon fontSize="large" color="primary" />
                                    <Typography variant="caption" color="text.secondary">
                                      Photo {index + 1}
                                    </Typography>
                                  </Stack>
                                )}
                              </Box>
                            </label>
                            {hasPreview && (
                              <Button
                                variant="text"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon fontSize="small" />}
                                onClick={() => handleRemoveGalleryPhoto(index)}
                              >
                                Remove
                              </Button>
                            )}
                          </Stack>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Box>

              {/* Language Selector */}
              {languageSelectorVisible && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  border: '1px solid',
                  borderColor: 'divider',
                }}>
                  <LanguageSwitcher onLanguageChange={handleLanguageChange} />
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      saveFormDataToSession({ ...formData, hideLanguageSelector: true });
                      setLang(i18n.language);
                      setLanguageSelectorVisible(false);
                    }}
                  >
                    {isDesktop ? t("saveLanguage") : t("saveLanguageMobile")}
                  </Button>
                </Box>
              )}

              {/* Form Sections */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 2 } }}>
                {/* Personal Details */}
                <Card sx={{ borderRadius: 1 }}>
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FontAwesomeIcon icon={faBookmark} />
                        <Typography variant="h6">{t("step1")}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {/* Render default personal fields */}
                        {defaultPersonalFields.map((field) => (
                          <Grid item xs={12} sm={6} key={field}>
                            <CustomTextField
                              label={t(field)}
                              name={field}
                              value={formData[field] || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                        ))}

                        {/* Render additional fields based on visibility */}
                        {formData.personalDetailsOrder && formData.personalDetailsOrder.map((field) => {
                          if (defaultPersonalFields.includes(field)) return null;
                          if (!visibleFields.personal[field]) return null;

                          return (
                            <Grid item xs={12} sm={6} key={field}>
                              {field === "dateOfBirth" ? (
                                <DatePicker
                                  sx={{
                                    width: "100%",
                                  }}
                                  label={t("dateOfBirth")}
                                  value={formData.dateOfBirth || null}
                                  onChange={handleDateChange}
                                  format="DD/MM/YYYY"
                                  renderInput={(params) => (
                                    <CustomTextField
                                      {...params}
                                      name={field}
                                      value={formData[field]}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                  )}
                                />
                              ) : field === "birthTime" ? (
                                <TimeClockWithTextField
                                  label={t(field)}
                                  value={formData.birthTime || null}
                                  onChange={handleTimeChange}
                                />
                              ) : field === "height" ? (
                                <HeightSelect
                                  label={t("height")}
                                  name={field}
                                  value={formData[field]}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              ) : (
                                <CustomTextField
                                  label={t(field)}
                                  name={field}
                                  value={formData[field]}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              )}
                            </Grid>
                          );
                        })}
                      </Grid>
                      {renderPersonalDetailsTags()}

                      {/* Additional Personal Details */}
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                          Additional Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {t("additionalDetailsText")}
                        </Typography>
                        {formData.personalAdditions && formData.personalAdditions.map((addition, index) => (
                          <Box key={index} sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={5}>
                                <CustomTextField
                                  label={t("newFieldKey")}
                                  value={addition.key}
                                  onChange={(e) => handleFieldChange("personal", index, "key", e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={5}>
                                <CustomTextField
                                  label={t("newFieldValue")}
                                  value={addition.value}
                                  onChange={(e) => handleFieldChange("personal", index, "value", e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={2}>
                                <IconButton
                                  onClick={() => handleRemoveField("personal", index)}
                                  color="error"
                                  sx={{ mt: 1 }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => handleAddField("personal")}
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          {t("addField")}
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Card>

                {/* Family Details */}
                <Card sx={{ borderRadius: 1 }}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FontAwesomeIcon icon={faBookmark} />
                        <Typography variant="h6">{t("step2")}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {defaultFamilyFields.map((field) => (
                          <Grid item xs={12} sm={6} key={field}>
                            <CustomTextField
                              label={t(field)}
                              name={field}
                              value={formData[field] || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                        ))}
                        {formData.familyDetailsOrder && formData.familyDetailsOrder.map((field) => {
                          if (defaultFamilyFields.includes(field)) return null;
                          if (!visibleFields.family[field]) return null;

                          return (
                            <Grid item xs={12} sm={6} key={field}>
                              <CustomTextField
                                label={t(field)}
                                name={field}
                                value={formData[field] || ""}
                                onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                      {renderFamilyDetailsTags()}

                      {/* Additional Family Details */}
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                          Additional Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {t("additionalDetailsText")}
                        </Typography>
                        {formData.familyAdditions && formData.familyAdditions.map((addition, index) => (
                          <Box key={index} sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={5}>
                                <CustomTextField
                                  label={t("newFieldKey")}
                                  value={addition.key}
                                  onChange={(e) => handleFieldChange("family", index, "key", e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={5}>
                                <CustomTextField
                                  label={t("newFieldValue")}
                                  value={addition.value}
                                  onChange={(e) => handleFieldChange("family", index, "value", e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={2}>
                                <IconButton
                                  onClick={() => handleRemoveField("family", index)}
                                  color="error"
                                  sx={{ mt: 1 }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => handleAddField("family")}
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          {t("addField")}
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Card>

                {/* Contact Details */}
                <Card sx={{ borderRadius: 1 }}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FontAwesomeIcon icon={faBookmark} />
                        <Typography variant="h6">{t("step3")}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {defaultContactFields.map((field) => (
                          <Grid item xs={12} sm={6} key={field}>
                            <CustomTextField
                              label={t(field)}
                              name={field}
                              value={formData[field] || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                        ))}
                        {formData.contactDetailsOrder && formData.contactDetailsOrder.map((field) => {
                          if (defaultContactFields.includes(field)) return null;
                          if (!visibleFields.contact[field]) return null;

                          return (
                            <Grid item xs={12} sm={6} key={field}>
                              <CustomTextField
                                label={t(field)}
                                name={field}
                                value={formData[field] || ""}
                                onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                      {renderContactDetailsTags()}

                      {/* Additional Contact Details */}
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                          Additional Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {t("additionalDetailsText")}
                        </Typography>
                        {formData.contactAdditions && formData.contactAdditions.map((addition, index) => (
                          <Box key={index} sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={5}>
                                <CustomTextField
                                  label={t("newFieldKey")}
                                  value={addition.key}
                                  onChange={(e) => handleFieldChange("contact", index, "key", e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={5}>
                                <CustomTextField
                                  label={t("newFieldValue")}
                                  value={addition.value}
                                  onChange={(e) => handleFieldChange("contact", index, "value", e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={2}>
                                <IconButton
                                  onClick={() => handleRemoveField("contact", index)}
                                  color="error"
                                  sx={{ mt: 1 }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => handleAddField("contact")}
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          {t("addField")}
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Card>

                {/* Template Picker */}
                <Card sx={{ borderRadius: 1 }}>
                  <Box sx={{ p: { xs: 1, sm: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <FontAwesomeIcon icon={faBookmark} />
                      <Typography variant="h6">{t("step4")}</Typography>
                    </Box>
                                        <TemplatePicker
                      onTemplateSelect={onTemplateSelection}
                      uuid={uuid}
                      reloadTrigger={templateReloadTrigger}
                    />
                  </Box>
                </Card>

                {/* Action Buttons */}
                <Box sx={{ 
                  mt: { xs: 3, sm: 4 }, 
                  mb: { xs: 2, sm: 3 },
                  p: { xs: 2.5, sm: 3.5 },
                  bgcolor: 'background.paper', 
                  borderRadius: { xs: 2, sm: 3 },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.04), 0 4px 8px rgba(0,0,0,0.03)',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.2),
                }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.5, sm: 2 }}
                    sx={{
                      '& .MuiButton-root': {
                        minHeight: { xs: 44, sm: 52 },
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' },
                        letterSpacing: '0.02em',
                        px: { xs: 2.5, sm: 3.5 },
                        py: { xs: 1.25, sm: 1.5 },
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1), 0 4px 8px rgba(0,0,0,0.08)',
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.15), 0 8px 16px rgba(0,0,0,0.12)',
                          transform: { xs: 'none', sm: 'translateY(-2px)' },
                        },
                        '&:active': {
                          transform: { xs: 'scale(0.98)', sm: 'translateY(0px) scale(0.98)' },
                        },
                      }
                    }}
                  >
                    {/* Download PDF Button */}
                    <Button
                      variant="contained"
                      onClick={downloadBiodata}
                      startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                      fullWidth={{ xs: true, sm: false }}
                      disabled={generatingPdf}
                      sx={{
                        flex: { xs: 1, sm: '1 1 auto' },
                        bgcolor: '#22c55e',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#16a34a',
                        },
                        '&:disabled': {
                          bgcolor: alpha(theme.palette.grey[400], 0.6),
                          color: alpha(theme.palette.common.white, 0.7),
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {isPurchased ? t("download") : t("downloadFree")}
                    </Button>

                    {/* Buy Now Button */}
                    {!isTemplateFree && !isPurchased && (
                      <Button
                        variant="contained"
                        onClick={buyNow}
                        startIcon={<FontAwesomeIcon icon={faCreditCard} />}
                        fullWidth={{ xs: true, sm: false }}
                        sx={{
                          flex: { xs: 1, sm: '1 1 auto' },
                          bgcolor: '#3b82f6',
                          color: 'white',
                          '&:hover': {
                            bgcolor: '#2563eb',
                          },
                        }}
                      >
                        {t('buyNow')}
                      </Button>
                    )}

                    {/* Reset Form Button */}
                    <Button
                      variant="contained"
                      onClick={handleReset}
                      startIcon={<DeleteIcon />}
                      fullWidth={{ xs: true, sm: false }}
                      sx={{
                        flex: { xs: 1, sm: '1 1 auto' },
                        bgcolor: '#ef4444',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#dc2626',
                        },
                      }}
                    >
                      {t("resetForm")}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Grid>

            {/* Sidebar - Desktop Only */}
            <Grid item xs={12} lg={3}>
              <Box sx={{
                display: { xs: 'none', lg: 'block' },
                position: 'sticky',
                top: 100,
              }}>
                {/* Photo Section - Desktop */}
                <Card sx={{ p: 2, mb: 2, borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                    {t("profilePhoto")}
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: 'none' }}
                      id="photo-upload-desktop"
                    />
                    <label htmlFor="photo-upload-desktop">
                      <Avatar
                        src={photoPreview}
                        sx={{
                          width: 160,
                          height: 160,
                          cursor: 'pointer',
                          border: '2px dashed',
                          borderColor: 'primary.main',
                          bgcolor: 'background.paper',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          mb: 1.5,
                          '&:hover': {
                            transform: 'scale(1.02)',
                            transition: 'transform 0.2s ease-in-out',
                          }
                        }}
                      >
                        {!photoPreview && <AddIcon fontSize="large" />}
                      </Avatar>
                    </label>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, textAlign: 'center' }}>
                      {t("addPhotoMessage")}
                    </Typography>
                    {photoPreview && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleRemovePhoto}
                        size="small"
                        fullWidth
                      >
                        Remove Photo
                      </Button>
                    )}
                  </Box>
                </Card>

                {/* Gallery Section - Desktop */}
                <Card sx={{ p: 2, mb: 2, borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, textAlign: 'center' }}>
                    {t("photoGallery")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    {t("photoGalleryDescription")}
                  </Typography>
                  <Grid container spacing={2}>
                    {Array.from({ length: MAX_GALLERY_PHOTOS }).map((_, index) => {
                      const inputId = `gallery-upload-desktop-${index}`;
                      const hasPreview = !!galleryPreviews[index];
                      return (
                        <Grid item xs={6} key={`gallery-desktop-${index}`}>
                          <Stack spacing={1.5} alignItems="center">
                            <input
                              type="file"
                              accept="image/*"
                              id={inputId}
                              style={{ display: 'none' }}
                              onChange={(e) => handlePhotoChange(e, { type: 'gallery', index })}
                            />
                            <label htmlFor={inputId} style={{ width: '100%' }}>
                              <Box
                                sx={{
                                  width: '100%',
                                  height: 140,
                                  borderRadius: 2,
                                  border: '2px dashed',
                                  borderColor: hasPreview ? 'primary.light' : 'primary.main',
                                  bgcolor: 'background.paper',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  boxShadow: hasPreview ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
                                  transition: 'transform 0.2s ease-in-out',
                                  '&:hover': {
                                    transform: 'scale(1.02)',
                                  },
                                }}
                              >
                                {galleryLoading[index] && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      inset: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: 'rgba(0,0,0,0.45)',
                                    }}
                                  >
                                    <CircularProgress size={30} sx={{ color: '#fff' }} />
                                  </Box>
                                )}
                                {hasPreview ? (
                                  <Box
                                    component="img"
                                    src={galleryPreviews[index]}
                                    alt={`Gallery ${index + 1}`}
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      objectPosition: 'center',
                                    }}
                                  />
                                ) : (
                                  <Stack spacing={1} alignItems="center">
                                    <AddIcon fontSize="large" color="primary" />
                                    <Typography variant="caption" color="text.secondary">
                                      Photo {index + 1}
                                    </Typography>
                                  </Stack>
                                )}
                              </Box>
                            </label>
                            {hasPreview && (
                              <Button
                                variant="text"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon fontSize="small" />}
                                onClick={() => handleRemoveGalleryPhoto(index)}
                              >
                                Remove
                              </Button>
                            )}
                          </Stack>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Card>

                {/* Progress Indicator */}
                <Card sx={{ p: 2, borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Form Progress
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: formData.name ? 'success.main' : 'grey.300'
                      }} />
                      <Typography variant="body2">Personal Details</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: formData.fatherName ? 'success.main' : 'grey.300'
                      }} />
                      <Typography variant="body2">Family Details</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: formData.contactPerson ? 'success.main' : 'grey.300'
                      }} />
                      <Typography variant="body2">Contact Details</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: selectedTemplate ? 'success.main' : 'grey.300'
                      }} />
                      <Typography variant="body2">Template Selected</Typography>
                    </Box>
                  </Box>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Container>

        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />

        <CropperDialog
          open={showCropper}
          onClose={handleCropperClose}
          imageSrc={cropperImageSrc}
          aspectRatio={cropperAspectRatio}
          onSave={handleCropSave}
        />

        {/* Floating Save Button */}
        <Zoom in={showFloatingSave}>
          <Fab
            aria-label="save"
            onClick={handleSave}
            disabled={isFormSaved}
            sx={{
              position: 'fixed',
              top: { xs: 80, sm: 90 },
              right: { xs: 16, sm: 24 },
              zIndex: 1000,
              bgcolor: isFormSaved ? 'rgba(0, 0, 0, 0.26)' : '#E74C3C',
              color: 'white',
              boxShadow: isFormSaved 
                ? 'none' 
                : '0 4px 12px rgba(231, 76, 60, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)',
              '&:hover': {
                bgcolor: isFormSaved ? 'rgba(0, 0, 0, 0.26)' : '#C0392B',
                boxShadow: isFormSaved 
                  ? 'none' 
                  : '0 6px 16px rgba(231, 76, 60, 0.5), 0 4px 8px rgba(0, 0, 0, 0.3)',
                transform: isFormSaved ? 'none' : 'scale(1.05)',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(0, 0, 0, 0.26)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <SaveIcon />
          </Fab>
        </Zoom>
      </Box>
    </LocalizationProvider>
  );
};

export default BiodataForm;